import React from 'react';
import RoddoSectionCard from './layout/RoddoSectionCard';
import { getRoddoUiTokens, resolveRoddoDarkMode } from '../constants/theme';
import baleenBayBazaarComingSoon from '../assets/backgrounds/baleenbaybazaarcomingsoon.png';

type Props = {
  onBack?: () => void;
  logoNode?: React.ReactNode;
  logoSrc?: string;
  title?: string;
  kicker?: string;
  message?: string;
  ctaLabel?: string;
  onCta?: () => void;
  backLabel?: string;
  isDarkMode?: boolean;
};

export default function BazaarComingSoonView({
  onBack,
  title = 'Baleen Bay Bazaar',
  kicker = 'Coming Soon',
  message = 'We’re polishing this feature for launch.',
  ctaLabel,
  onCta,
  backLabel = 'Back',
  isDarkMode,
}: Props) {
  const dark = resolveRoddoDarkMode(isDarkMode);
  const ui = getRoddoUiTokens(dark);
  const showCta = typeof onCta === 'function' && !!ctaLabel;

  return (
    <div
      className="relative h-full min-h-0 w-full overflow-hidden"
      style={{ color: ui.textPrimary }}
    >
      <div className="absolute inset-0" aria-hidden="true">
        <img
          src={baleenBayBazaarComingSoon}
          alt=""
          className="h-full w-full object-cover"
          style={{ objectPosition: 'center center' }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: dark
              ? 'radial-gradient(90% 60% at 50% 0%, rgba(118,87,232,0.11), transparent 60%), linear-gradient(180deg, rgba(9,11,19,0.08), rgba(14,16,32,0.20), rgba(9,11,19,0.44))'
              : 'linear-gradient(180deg, rgba(255,247,235,0.02), rgba(255,247,235,0.08), rgba(255,239,221,0.16))',
          }}
        />
      </div>

      <div
        className="relative z-10 flex h-full min-h-0 items-center justify-center px-5 pt-4 sm:px-6"
        style={{
          paddingBottom: 'var(--roddo-page-bottom-clearance, calc(var(--roddo-bottom-nav-h, var(--roddo-nav-h, 76px)) + 18px))',
        }}
      >
        <RoddoSectionCard className="mx-auto w-full max-w-sm shrink-0 border-white/35 bg-white/74 px-5 py-7 text-center shadow-2xl backdrop-blur-md sm:px-6 sm:py-8">
          <div
            className="text-[16px] font-black uppercase tracking-[0.30em]"
            style={{ color: ui.textPrimary, opacity: 0.92 }}
          >
            {kicker}
          </div>

          <div
            className="mt-4 text-sm font-semibold leading-6"
            style={{ color: ui.textSecondary }}
          >
            {message}
          </div>

          {(onBack || showCta) && (
            <div className="mt-6 flex flex-col items-center justify-center gap-2 sm:flex-row">
              {onBack ? (
                <button
                  type="button"
                  onClick={onBack}
                  className="inline-flex min-w-[96px] items-center justify-center rounded-2xl border px-4 py-2.5 font-semibold transition-transform active:translate-y-[1px]"
                  style={{
                    background: dark
                      ? 'rgba(118,87,232,0.16)'
                      : 'rgba(255,255,255,0.62)',
                    borderColor: dark
                      ? 'rgba(146,120,236,0.34)'
                      : ui.borderStrong,
                    color: dark ? '#E7E0FF' : ui.textPrimary,
                    boxShadow: dark
                      ? '0 8px 18px rgba(0,0,0,0.20), inset 0 1px 0 rgba(255,255,255,0.06)'
                      : `${ui.shadow}, inset 0 2px 0 rgba(255,255,255,0.22)`,
                  }}
                >
                  {backLabel}
                </button>
              ) : null}

              {showCta ? (
                <button
                  type="button"
                  onClick={onCta}
                  className="inline-flex items-center justify-center rounded-2xl border px-4 py-2.5 font-extrabold transition-transform active:translate-y-[1px]"
                  style={{
                    background: 'linear-gradient(180deg, #7c6ff7, #6657ef)',
                    borderColor: 'rgba(255,255,255,0.16)',
                    color: '#ffffff',
                    boxShadow: '0 10px 24px rgba(102,87,239,0.30)',
                  }}
                >
                  {ctaLabel}
                </button>
              ) : null}
            </div>
          )}
        </RoddoSectionCard>

        <span className="sr-only">{title}</span>
      </div>
    </div>
  );
}
